import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from '../styles/HomePage.module.css';
// Import icons
import musicIcon from '../images/Professional-Quality.png'; 
import giftIcon from '../images/Unforgettable-Gift.png';   
import heartIcon from '../images/Deeply-Personal.png'; 
// Import components
import MusicGenreExplorer from '../components/MusicGenreExplorer';
import CommunityGivebackSection from '../components/CommunityGivebackSection';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Sample testimonials array
  const testimonials = [
    {
      text: "I surprised my wife on our 40th anniversary with a custom song about our journey together. She was in tears - it captured our story perfectly!",
      author: "Robert, 69"
    },
    {
      text: "The song created for my daughter's wedding was beyond what I could have imagined. Everyone was asking how we found such a special gift!",
      author: "Sarah, 52"
    },
    {
      text: "MakeMeSongs helped me tell my girlfriend how much I love her through music. She said yes to my proposal right after hearing it!",
      author: "Michael, 31"
    }
  ];

  const handleGetStarted = () => {
    navigate('/packages');
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const handlePlayJingle = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
      } else {
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(error => {
              console.error("Playback prevented by browser:", error);
              setIsPlaying(false);
            });
        }
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  return (
    <div className={styles.homeContainer}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>MakeMeSongs | Create Custom Personalized Songs For Special Occasions</title>
        <meta name="description" content="Transform your special moments into personalized, professionally produced songs. The perfect unique gift for weddings, anniversaries, birthdays and more." />
        <meta name="keywords" content="custom songs, personalized music, unique gifts, special occasion songs, custom music" />
        <meta property="og:title" content="MakeMeSongs | Create Custom Personalized Songs" />
        <meta property="og:description" content="Transform your special moments into personalized, professionally produced songs. The perfect unique gift." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com" />
        <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
        <link rel="canonical" href="https://makemesongs.com" />
      </Helmet>
      
      {/* Audio element */}
      <audio 
        ref={audioRef} 
        src="/audio/makemesongs-jingle.mp3" 
        onEnded={handleAudioEnded}
      />
      
      {/* Hero Section */}
      <div 
        className={styles.heroSection} 
        style={{
          background: 'linear-gradient(135deg, #7C3AED, #9D4EDD)',
        }}
      >
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1>The Simple Way to Create Custom Songs</h1>
            <p>Celebrate Life's Special Moments with a Personalized Song that captures your most meaningful memories.</p>
            
            <div className={styles.buttonContainer}>
              <button 
                className={styles.getStartedButton}
                onClick={handleGetStarted}
              >
                Create Your Song Now
              </button>
              
              <button 
                className={`${styles.playButton} ${isPlaying ? styles.playing : ''}`}
                onClick={handlePlayJingle}
                aria-label="Play sample jingle"
              >
                {isPlaying ? (
                  <>
                    <span className={styles.pauseIcon}></span>
                    <span>Listening...</span>
                  </>
                ) : (
                  <>
                    <span className={styles.playIcon}></span>
                    <span>Play Me!</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className={styles.heroImage}>
            <video
              autoPlay
              muted
              loop
              playsInline
              className={styles.heroVideoContent}
            >
              <source src="/videos/hero-video.mp4" type="video/mp4" />
              <source src="/videos/hero-video.webm" type="video/webm" />
              <img 
                src="/images/hero-image.png" 
                alt="Custom song creation" 
                className={styles.heroLogoImage} 
              />
            </video>
          </div>
        </div>
      </div>

      {/* Genre Explorer Section - Moved to be right after Hero Section */}
      <MusicGenreExplorer />

      {/* Why MakeMeSongs Section */}
      <div className={styles.whyChooseSection}>
        <h2>Why MakeMeSongs?</h2>
        <p className={styles.sectionSubheadline}>Transforming Memories into Melodies That Touch the Soul</p>
        
        <div className={styles.floatingNote1}></div>
        <div className={styles.floatingNote2}></div>
        
        <div className={styles.featuresGrid}>
          <div className={styles.feature}>
            <div className={styles.purpleTop}></div>
            <div className={styles.featureIcon}>
              <img src={musicIcon} alt="Music Note" className={styles.iconImage} />
            </div>
            <h3>Professional Quality</h3>
            <p>Each song is crafted with professional-grade production and vocals.</p>
          </div>
          <div className={styles.feature}>
            <div className={styles.purpleTop}></div>
            <div className={styles.featureIcon}>
              <img src={heartIcon} alt="Heart" className={styles.iconImage} />
            </div>
            <h3>Deeply Personal</h3>
            <p>Your story and memories are woven into every lyric and melody.</p>
          </div>
          <div className={styles.feature}>
            <div className={styles.purpleTop}></div>
            <div className={styles.featureIcon}>
              <img src={giftIcon} alt="Gift" className={styles.iconImage} />
            </div>
            <h3>Unforgettable Gift</h3>
            <p>Create a one-of-a-kind gift that will be cherished for years to come.</p>
          </div>
        </div>
        <button 
          className={styles.getStartedButton}
          onClick={handleGetStarted}
          style={{ marginTop: '40px' }}
        >
          Create Your Song Now
        </button>
      </div>

      {/* Simple divider */}
      <div className={styles.simpleDivider}></div>

      {/* How It Works Section */}
      <div className={styles.howItWorksSection}>
        <h2>How It Works - Simple & Easy</h2>
        <p className={styles.sectionSubheadline}>Bringing Your Story to Life, One Note at a Time</p>
        
        <div className={styles.floatingNote3}></div>
        <div className={styles.floatingNote4}></div>
        
        <div className={styles.stepsGrid}>
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <h3>Choose a Package</h3>
            <p>Select from our Basic, Premium, or Deluxe song packages.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <h3>Tell Your Story</h3>
            <p>Share the memories and details you want included in your song.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <h3>Select Music Style</h3>
            <p>Choose from a variety of musical genres and styles.</p>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>4</div>
            <h3>Receive Your Song</h3>
            <p>Within 24-48 hours, your custom song will be ready to download and share.</p>
          </div>
        </div>
        
        <button 
          className={styles.startMySongButton}
          onClick={handleGetStarted}
        >
          Start My Song
        </button>
      </div>

      {/* Testimonials Section */}
      <div className={styles.testimonialsSection}>
        <div className={styles.testimonialsInnerContainer}>
          <h2>What Our Customers Say</h2>
          <p className={styles.sectionSubheadline}>Real Stories, Extraordinary Musical Moments</p>
          
          <div className={styles.floatingNote1}></div>
          <div className={styles.floatingNote2}></div>
          <div className={styles.floatingNote3}></div>
          <div className={styles.floatingNote4}></div>
          
          <div className={styles.testimonialCarouselContainer}>
            <button 
              className={`${styles.carouselButton} ${styles.mobileTopButton}`} 
              onClick={prevTestimonial}
              aria-label="Previous testimonial"
            >
              ‹
            </button>
            
            <div className={styles.testimonialCarousel}>
              <button 
                className={`${styles.carouselButton} ${styles.desktopLeftButton}`}
                onClick={prevTestimonial}
                aria-label="Previous testimonial"
              >
                ‹
              </button>
              
              <div className={styles.testimonial}>
                <p className={styles.testimonialText}>
                  "{testimonials[currentTestimonial].text}"
                </p>
                <p className={styles.testimonialAuthor}>
                  - {testimonials[currentTestimonial].author}
                </p>
              </div>
              
              <button 
                className={`${styles.carouselButton} ${styles.desktopRightButton}`}
                onClick={nextTestimonial}
                aria-label="Next testimonial"
              >
                ›
              </button>
            </div>
            
            <button 
              className={`${styles.carouselButton} ${styles.mobileBottomButton}`} 
              onClick={nextTestimonial}
              aria-label="Next testimonial"
            >
              ›
            </button>
          </div>
          
          <div className={styles.carouselDots}>
            {testimonials.map((_, index) => (
              <span 
                key={index}
                className={`${styles.dot} ${currentTestimonial === index ? styles.activeDot : ''}`}
                onClick={() => setCurrentTestimonial(index)}
              ></span>
            ))}
          </div>
        </div>
      </div>
      
      {/* Community Giveback Section */}
      <CommunityGivebackSection />
      
      {/* CTA Section */}
      <div className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>Ready to Create Your Custom Song?</h2>
          <p>Turn your special moments into music that will last forever.</p>
          <button 
            className={styles.getStartedButton}
            onClick={handleGetStarted}
          >
            Start Creating Your Song
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;